/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { createAction, props } from '@ngrx/store';
import { Message, MessageType } from 'CalltakingCoreApi';
import { PreDefinedMessage, PredefinedMessages } from '../model/pre-defined-messages';
import { Topic } from '../../user/model/multiple-subscriptions';
import { TranslationSettings, TranslationLanguage } from '../model/translation.model';
import { DialogState } from '../model/dialog.model';
import { LanguageChangeSource } from '../model/language.model';

export const messageEffectsInitialized = createAction('[Message] Effects Initialized');
export const updateMessageSubscriptions = createAction(
    '[Message] Stomp Status Update',
    props<{ subscriptions: { [topic: string]: { [receipt: string]: boolean } } }>()
);

export const updateMultipleMessageSubscriptions = createAction(
    '[Message] Multiple Stomp Status Update',
    props<{ topic: Topic, clusterName: string, status: boolean }>()
);

export const initializeSmsMessageState = createAction('[Message] SMS State Initialization', props<{ clusterName?: string }>());
export const fetchSmsMessages = createAction('[Message] SMS Fetch', props<{ callId: string, clusterName: string }>());
export const fetchSmsMessagesSuccess = createAction('[Message] SMS Fetch Success', props<{ messages: Message[] }>());
export const fetchSmsMessagesFail = createAction('[Message] SMS Fetch Fail', props<{ payload: string }>());
export const fetchPredefinedSmsMessages = createAction('[Message] Fetch Predefined SMS ');
export const fetchPredefinedSmsMessagesSuccess = createAction('[Message] Fetch Predefined SMS Success', props<{ messages: PredefinedMessages[] }>());
export const fetchPredefinedSmsMessagesFail = createAction('[Message] Fetch Predefined SMS Fail', props<{ payload: string }>());
export const newSmsMessage = createAction('[Message] SMS New', props<{ message: Message, clusterName?: string }>());
export const updateSmsMessage = createAction('[Message] SMS Update', props<{ message: Message, clusterName?: string }>());
export const deleteSmsMessage = createAction('[Message] SMS Delete', props<{ message: Message, clusterName?: string }>());

export const fetchRealtimeMessages = createAction('[Message] Realtime Fetch', props<{ callId: string, clusterName: string }>());
export const initializeTddMessageState = createAction('[Message] TDD State Initialization', props<{ clusterName?: string }>());
export const fetchTddMessages = createAction('[Message] TDD Fetch', props<{ callId: string, clusterName: string }>());
export const fetchTddMessagesSuccess = createAction('[Message] TDD Fetch Success', props<{ messages: Message[] }>());
export const fetchTddMessagesFail = createAction('[Message] TDD Fetch Fail', props<{ payload: string }>());
export const fetchPredefinedTddMessages = createAction('[Message] Fetch Predefined TDD');
export const fetchPredefinedTddMessagesSuccess = createAction('[Message] Fetch Predefined TDD Success', props<{ messages: PredefinedMessages[] }>());
export const fetchPredefinedTddMessagesFail = createAction('[Message] Fetch Predefined TDD Fail', props<{ payload: string }>());
export const newTddMessage = createAction('[Message] TDD New', props<{ message: Message, clusterName?: string }>());
export const updateTddMessage = createAction('[Message] TDD Update', props<{ message: Message, clusterName?: string }>());
export const deleteTddMessage = createAction('[Message] TDD Delete', props<{ message: Message, clusterName?: string }>());

export const initializeRttMessageState = createAction('[Message] RTT State Initialization', props<{ clusterName?: string }>());
export const fetchRttMessages = createAction('[Message] RTT Fetch', props<{ callId: string, clusterName: string }>());
export const fetchRttMessagesSuccess = createAction('[Message] RTT Fetch Success', props<{ messages: Message[] }>());
export const fetchRttMessagesFail = createAction('[Message] RTT Fetch Fail', props<{ payload: string }>());
export const newRttMessage = createAction('[Message] RTT New', props<{ message: Message, clusterName?: string }>());
export const updateRttMessage = createAction('[Message] RTT Update', props<{ message: Message, clusterName?: string }>());
export const deleteRttMessage = createAction('[Message] RTT Delete', props<{ message: Message, clusterName?: string }>());

export const fetchReleasedCallMessages = createAction('[Message] Released Call Messages Fetch', props<{ callId: string }>());
export const fetchReleasedCallMessagesSuccess = createAction('[Message] Released Call Messages Fetch Success', props<{ messages: Message[] }>());
export const fetchReleasedCallMessagesFail = createAction('[Message] Released Call Messages Fetch Fail', props<{ payload: string }>());

export const newSystemErrorMessage = createAction('[Message] System Error New', props<{ message: Message }>());
export const updateSystemErrorMessage = createAction('[Message] System Error Update', props<{ message: Message }>());
export const deleteSystemErrorMessage = createAction('[Message] System Error Delete', props<{ message: Message }>());

export const initializeDtmfMessageState = createAction('[Message] DTMF State Initialization', props<{ clusterName: string }>());
export const fetchDtmfMessages = createAction('[Message] DTMF Fetch', props<{ callId: string, clusterName: string }>());
export const fetchDtmfMessagesSuccess = createAction('[Message] DTMF Fetch Success', props<{ messages: Message[] }>());
export const fetchDtmfMessagesFail = createAction('[Message] DTMF Fetch Fail', props<{ payload: string }>());
export const fetchPredefinedDtmfMessages = createAction('[Message] Fetch Predefined DTMF');
export const newDtmfMessage = createAction('[Message] DTMF New', props<{ message: Message, clusterName: string }>());
export const updateDtmfMessage = createAction('[Message] DTMF Update', props<{ message: Message, clusterName: string }>());
export const deleteDtmfMessage = createAction('[Message] DTMF Delete', props<{ message: Message, clusterName: string }>());

export const purgeMessages = createAction('[Message] Purge Messages for Call', props<{ uuids: string[] }>());

export const fetchTextCallHistory = createAction(
    '[Message] Fetch Historical Texts Messages for Call',
    props<{ callingNumber: string; callId: string; nenaCallId: string }>()
);
export const fetchTextCallHistorySuccess = createAction(
    '[Message] Fetch Historical Texts Messages for Call Success',
    props<{ referenceKey: string; messages: Message[] }>()
);
export const fetchTextCallHistoryFail = createAction('[Message] Fetch Historical Texts Messages for Call Fail', props<{ payload: string }>());

export const tddChallenge = createAction(
    '[Message] TDD Challenge', props<{
        callId: string,
        clusterName: string,
        predefined: boolean,
        language: string,
        message: string
    }>()
)

export enum MessageErrorType {
    TRANSLATION = 'translation',
    SEND = 'send',
    DELIVERY = 'delivery'
}

export const fetchTranslationLanguagesSuccess = createAction(
    '[Message] Fetch Translation Languages Success',
    props<{ languages: TranslationLanguage[] }>()
);
export const fetchTranslationLanguagesFail = createAction(
    '[Message] Fetch Translation Languages Fail',
    props<{ payload: string }>()
);
export const updateTranslationSettings = createAction(
    '[Message] Update Translation Settings',
    props<{ settings: TranslationSettings }>()
);
export const messageErrorTranslationFailed = createAction(
    '[Message] Translation Failed',
    props<{ messageType: MessageType; message: string; callId: string; clusterName: string; predefined: boolean; language: string; messageId: string, userName: string }>()
);
export const messageErrorMarkTranslationError = createAction(
    '[Message] Mark As Translation Error',
    props<{
        messageId: string;
        messageType: MessageType;
        message: string;
        callId: string;
        clusterName: string;
        predefined: boolean;
        language: string;
        userName?: string;
    }>()
);
export const messageErrorRemoveTranslationError = createAction(
    '[Message] Translation Error — Send Without Translation',
    props<{ messageId: string; messageType: MessageType; message: string; callId: string; clusterName: string; predefined: boolean; language: string }>()
);

export const messageErrorRetry = createAction(
    '[Message] Message Error — Retry',
    props<{
        messageId: string;
        errorType: MessageErrorType;
        messageType: MessageType;
        message: string;
        callId: string;
        clusterName: string;
        predefined: boolean;
        language: string;
        username?: string;
        sourceText?: string;
        sourceLanguage?: string;
        targetText?: string;
        targetLanguage?: string;
        messageLanguage?: string;
    }>()
);
export const messageErrorRemove = createAction(
    '[Message] Message Error — Remove',
    props<{
        messageId: string;
        errorType: MessageErrorType }>()
);
export const pendingMessageAdded = createAction(
    '[Message] Pending Message Added',
    props<{ message: Message; messageType: MessageType }>()
);
export const pendingMessageTranslated = createAction(
    '[Message] Pending Message Translated',
    props<{
        messageId: string;
        message?: string;
        sourceText?: string;
        sourceLanguage?: string;
        targetText?: string;
        targetLanguage?: string;
        messageLanguage?: string;
    }>()
);
export const pendingMessageRemove = createAction(
    '[Message] Pending Message Remove',
    props<{ messageId: string }>()
);
export const fetchDialogState = createAction(
    '[Message] Fetch Dialog State',
    props<{ callId: string }>());
export const fetchDialogStateSuccess = createAction(
    '[Message] Fetch Dialog State Success',
    props<{ callId: string; dialogState: DialogState }>());
export const fetchDialogStateFail = createAction(
    '[Message] Fetch Dialog State Fail',
    props<{ callId: string; payload: string }>());

export const patchDialogLanguage = createAction(
    '[Message] Patch Dialog Language',
    props<{ callId: string; languageCode: string; languageChangeSource: LanguageChangeSource }>()
);
export const patchDialogLanguageSuccess = createAction(
    '[Message] Patch Dialog Language Success',
    props<{ callId: string; languageCode: string; languageChangeSource: LanguageChangeSource }>()
);
export const patchDialogLanguageFail = createAction(
    '[Message] Patch Dialog Language Fail',
    props<{ callId: string; languageCode: string; error: string }>()
);
export const bannerLanguageChanged = createAction(
    '[Message] Banner Language Changed',
    props<{ callId: string; languageCode: string | null; languageChangeSource: LanguageChangeSource }>()
);

